import { z } from 'zod';
import {
  DocumentMetadataSchema,
  IdSchema,
  IsoDateTimeSchema,
  LogicalDataTypeSchema,
  SchemaVersionSchema,
} from './common.js';
import { A1RangeSchema, LocationSchema } from './data-source.js';

/**
 * Referencia al objeto fisico que alimenta una tabla del Dataset. Deliberadamente
 * NO admite SQL libre: solo objetos identificables de la fuente.
 */
export const DatasetSourceRefSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('table'),
    namespace: z.array(z.string().min(1)).optional(),
    name: z.string().min(1),
  }),
  z.strictObject({
    kind: z.literal('file'),
    path: LocationSchema,
    sheet: z.string().min(1).optional(),
    range: A1RangeSchema.optional(),
  }),
  z.strictObject({
    kind: z.literal('resource'),
    resource: z.string().min(1),
  }),
]);
export type DatasetSourceRef = z.infer<typeof DatasetSourceRefSchema>;

/** Metadatos de esquema de una columna fisica (resultado del descubrimiento). */
export const DatasetColumnSchema = z.strictObject({
  /** Nombre fisico; es lo que referencia `sourceColumn` en el Semantic Model. */
  name: z.string().min(1).max(256),
  nativeType: z.string().min(1).max(128).optional(),
  logicalType: LogicalDataTypeSchema,
  nullable: z.boolean().default(true),
  description: z.string().max(2000).optional(),
});
export type DatasetColumn = z.infer<typeof DatasetColumnSchema>;

export const DatasetTableSchema = z.strictObject({
  id: IdSchema,
  name: z.string().min(1),
  dataSourceId: IdSchema,
  source: DatasetSourceRefSchema,
  /** Politica de materializacion en el motor analitico local. */
  materialization: z.enum(['live', 'cached', 'imported']).default('live'),
  /** Vacio mientras no se haya ejecutado el descubrimiento. */
  columns: z.array(DatasetColumnSchema).default([]),
  primaryKey: z.array(z.string().min(1)).default([]),
  rowCountEstimate: z.number().int().nonnegative().optional(),
});
export type DatasetTable = z.infer<typeof DatasetTableSchema>;

export const DatasetRefreshSchema = z.strictObject({
  mode: z.enum(['manual', 'on-open', 'scheduled', 'live']).default('manual'),
  /** Solo aplica a `scheduled`. */
  intervalMinutes: z.number().int().positive().max(525_600).optional(),
  lastRefreshedAt: IsoDateTimeSchema.optional(),
  lastStatus: z.enum(['never', 'running', 'success', 'failed']).default('never'),
  lastError: z.string().max(2000).optional(),
});
export type DatasetRefresh = z.infer<typeof DatasetRefreshSchema>;

/** Capacidades efectivas del Dataset (derivadas de los conectores y la materializacion). */
export const DatasetCapabilitiesSchema = z.strictObject({
  queryPushdown: z.boolean().default(false),
  incrementalRefresh: z.boolean().default(false),
  sampling: z.boolean().default(false),
  maxRowsHint: z.number().int().positive().optional(),
});
export type DatasetCapabilities = z.infer<typeof DatasetCapabilitiesSchema>;

/**
 * Dataset: QUE conjunto de datos se usa. Agrupa tablas de una o varias fuentes con
 * sus metadatos de esquema, politica de actualizacion y capacidades.
 */
export const DatasetSchema = z.strictObject({
  schemaVersion: SchemaVersionSchema,
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  tables: z.array(DatasetTableSchema).min(1),
  refresh: DatasetRefreshSchema.prefault({}),
  capabilities: DatasetCapabilitiesSchema.prefault({}),
  metadata: DocumentMetadataSchema.optional(),
});
export type Dataset = z.infer<typeof DatasetSchema>;
export type DatasetInput = z.input<typeof DatasetSchema>;
